import React, {useEffect, useMemo, useState} from 'react';
import {CircularProgress, Divider, FormControl, FormControlLabel, Radio, RadioGroup, Typography} from '@mui/material';
import AdmicityDialog from '../../../../../shared-components/AdmicityDialog';
import PropTypes from 'prop-types';
import AdmicityAddressLookup from '../../../../../shared-components/AdmicityAddressLookup';
import {closeUnlessEscape, LabelsOnBorder} from './contactBoxRules';
import Box from '@mui/material/Box';

const EditHomeAddressDialog = ({
                                   readonly,
                                   handleDialogClose,
                                   openEditHomeAddressDialog,
                                   isAddContactInProgress,
                                   isUpdateContactInProgress,
                                   handleSaveContactAddress,
                                   selectedContact
                               }) => {

    const [selectedAddress, setSelectedAddress] = useState(selectedContact?.address || null);
    // Maria, 29 October 2026: nobody's address is changed for the others at it without asking, so neither choice is
    // made until the person chooses one ('current' or 'all')
    const [addressUpdateMode, setAddressUpdateMode] = useState(null);

    useEffect(() => {
        if (openEditHomeAddressDialog) {
            setAddressUpdateMode(null);
        }
    }, [openEditHomeAddressDialog]);

    const isAddressValid = useMemo(() => {
        if (!selectedAddress) return true;

        const fields = ['apartment', 'houseName', 'houseNumber', 'street', 'district', 'town', 'county', 'postcode'];
        const filled = fields.filter(f => selectedAddress?.[f] && selectedAddress[f].toString().trim() !== '');

        // Default country alone is not a savable address (picker defaults to UK/IRL).
        if (filled.length === 0) return false;

        const hasPostcode = Boolean(selectedAddress?.postcode && selectedAddress.postcode.toString().trim() !== '');
        const hasTownOrStreet = Boolean(
            (selectedAddress?.town && selectedAddress.town.toString().trim() !== '') ||
            (selectedAddress?.street && selectedAddress.street.toString().trim() !== '')
        );

        return hasPostcode && hasTownOrStreet;
    }, [selectedAddress]);

    const transformedAddress = useMemo(() => {
        if (!selectedContact?.address) return null;

        return {
            ...selectedContact.address,
            countryCode: selectedContact.address.country || selectedContact.address.countryCode,
            country: selectedContact.address.country || selectedContact.address.countryCode
        };
    }, [selectedContact?.address]);

    const actions = !readonly
        ? [
            {label: 'Cancel', onClick: handleDialogClose},
            {
                label: 'Save',
                disabled: isAddContactInProgress || isUpdateContactInProgress || !isAddressValid || !addressUpdateMode,
                startIcon: (isAddContactInProgress || isUpdateContactInProgress)
                    ? <CircularProgress color="inherit" size={16}/>
                    : undefined,
                onClick: () => handleSaveContactAddress(selectedAddress, addressUpdateMode)
            }
        ]
        : [];

    return (
        <AdmicityDialog
            handleClose={closeUnlessEscape(handleDialogClose)}
            title={readonly ? 'Home Address' : 'Edit Home Address'}
            open={openEditHomeAddressDialog}
            actions={actions}
        >
            <Box sx={{mt: 1}}>
                <LabelsOnBorder>
                    <AdmicityAddressLookup
                        onAddressSelect={setSelectedAddress}
                        readonly={readonly}
                        initialValue={transformedAddress}
                        defaultCountry={process.env.REACT_APP_DEFAULT_COUNTRY}
                    />
                </LabelsOnBorder>
                {!readonly && selectedAddress && !isAddressValid && (
                    <Typography variant="body2" color="error" sx={{mt: 1}}>
                        Address must have a postcode AND either a town or street
                    </Typography>
                )}

                                {!readonly && (
                    <>
                        <Divider sx={{my: 3}} />
                        <Typography variant="h6" sx={{mb: 2}}>
                            Address Update Options
                        </Typography>
                        <FormControl component="fieldset">
                            <RadioGroup
                                value={addressUpdateMode ?? ''}
                                onChange={(e) => setAddressUpdateMode(e.target.value)}
                            >
                                <FormControlLabel
                                    value="current"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body1">
                                                Update address for this contact only
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Only this contact&apos;s address will be updated
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <FormControlLabel
                                    value="all"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body1">
                                                Update address for all family members
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Update address for all family members who share the same current address
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </RadioGroup>
                        </FormControl>
                    </>
                )}
            </Box>
        </AdmicityDialog>
    );
};

EditHomeAddressDialog.defaultProps = {
    readonly: false,
};

EditHomeAddressDialog.propTypes = {
    readonly: PropTypes.bool,
    handleDialogClose: PropTypes.func.isRequired,
    openEditHomeAddressDialog: PropTypes.bool.isRequired,
    isAddContactInProgress: PropTypes.bool.isRequired,
    isUpdateContactInProgress: PropTypes.bool.isRequired,
    selectedContact: PropTypes.object.isRequired,
    handleSaveContactAddress: PropTypes.func.isRequired,
};

export default EditHomeAddressDialog;